/**
 * Booking Data Mappers
 * Converte dati dal formato backend (UPPERCASE) al formato UI (camelCase)
 */

import {
  Activity as BackendActivity,
  Resource as BackendResource,
  Availability as BackendAvailability
} from '../services/api/BookingService';
import { Appointment as BackendAppointmentFull } from '../services/api/AppointmentService';
import { Activity, Resource, Availability, AppointmentUI } from './types';

// ============================================================================
// BACKEND TO UI MAPPERS
// ============================================================================

/**
 * Mappa Activity dal formato backend al formato UI
 * Supporta sia formato NMS (minuscolo) che legacy (UPPERCASE)
 */
export const mapActivityToUI = (backendActivity: BackendActivity): Activity => ({
  id: backendActivity.id || backendActivity.ACTIVITY_LID || '',
  name: backendActivity.name || backendActivity.ACTIVITY_NAME || '',
  group: backendActivity.group || backendActivity.ACTIVITY_GROUP_NAME || '',
  groupId: backendActivity.groupId || backendActivity.ACTIVITY_GROUP_LID || '',
  notice: backendActivity.notice || backendActivity.ACTIVITY_NOTICE,
  preparation: backendActivity.preparation || backendActivity.ACTIVITY_PREPARATION,
  duration: backendActivity.duration || backendActivity.ACTIVITY_DURATION,
  price: backendActivity.price || backendActivity.ACTIVITY_PRICE,
  ACTIVITY_PREPARATION: backendActivity.preparation || backendActivity.ACTIVITY_PREPARATION // Mantieni per retrocompatibilità
});

/**
 * Mappa array di Activity dal backend all'UI
 */
export const mapActivitiesToUI = (backendActivities: BackendActivity[]): Activity[] => {
  return backendActivities.map(mapActivityToUI);
};

/**
 * Mappa Resource dal formato backend al formato UI.
 * Backend serializza in camelCase (System.Text.Json ignora [JsonProperty]); fallback su UPPERCASE.
 */
export const mapResourceToUI = (backendResource: any): Resource => ({
  id: backendResource.id ?? backendResource.RESOURCE_LID,
  locationId: backendResource.locationId ?? backendResource.LOCATION_LID,
  resourceId: backendResource.resourceId ?? backendResource.RESOURCE_ID_NUMBER,
  name: backendResource.firstName ?? backendResource.RESOURCE_FIRST_NAME,
  surname: backendResource.secondName ?? backendResource.RESOURCE_SECOND_NAME,
  notice: backendResource.notice ?? backendResource.RESOURCE_NOTICE,
  email: backendResource.eMail ?? backendResource.email ?? backendResource.RESOURCE_EMAIL,
  phone: backendResource.mobilePhone ?? backendResource.RESOURCE_MOBILE_PHONE
});

/**
 * Mappa array di Resource dal backend all'UI
 */
export const mapResourcesToUI = (backendResources: BackendResource[]): Resource[] => {
  return backendResources.map(mapResourceToUI);
};

/**
 * Mappa Availability dal formato backend al formato UI.
 * Il backend espone i campi in camelCase (id, date, startTime, ...) anche se i modelli
 * C# hanno [JsonProperty("AVA_DATE")] di Newtonsoft (ignorato da System.Text.Json).
 * Per compatibilità leggiamo entrambi i formati con fallback.
 */
export const mapAvailabilityToUI = (backendAvailability: any): Availability => ({
  id: backendAvailability.id ?? backendAvailability.AVAILABILITY_LID,
  date: backendAvailability.date ?? backendAvailability.AVA_DATE,
  startTime: backendAvailability.startTime ?? backendAvailability.AVA_START_TIME,
  endTime: backendAvailability.endTime ?? backendAvailability.AVA_END_TIME,
  resourceId: backendAvailability.resourceId ?? backendAvailability.RESOURCE_LID,
  activityId: backendAvailability.activityId ?? backendAvailability.ACTIVITY_LID,
  insuranceId: backendAvailability.insuranceId ?? backendAvailability.INSURANCE_LID,
  locationId: backendAvailability.locationId ?? backendAvailability.LOCATION_LID,
  price: backendAvailability.price ?? backendAvailability.AVA_PRICE
});

/**
 * Mappa array di Availability dal backend all'UI
 */
export const mapAvailabilitiesToUI = (backendAvailabilities: BackendAvailability[]): Availability[] => {
  return backendAvailabilities.map(mapAvailabilityToUI);
};

/**
 * Mappa Appointment dal formato backend al formato UI.
 * Backend serializza in camelCase (System.Text.Json ignora [JsonProperty]); fallback su UPPERCASE.
 */
export const mapAppointmentToUI = (backendAppointment: any): AppointmentUI => ({
  id: backendAppointment.id ?? backendAppointment.appId ?? backendAppointment.APP_LID,
  status: backendAppointment.status ?? backendAppointment.APP_STATUS,
  startTime: backendAppointment.startTime ?? backendAppointment.APP_START_TIME,
  endTime: backendAppointment.endTime ?? backendAppointment.APP_END_TIME,
  date: backendAppointment.appointmentDate ?? backendAppointment.date ?? backendAppointment.APP_DATE,
  creationDate: backendAppointment.creationDate ?? backendAppointment.APP_CREATION_DATE,
  price: backendAppointment.price ?? backendAppointment.APP_PRICE,
  resourceId: backendAppointment.resourceId ?? backendAppointment.RESOURCE_LID,
  activityId: backendAppointment.activityId ?? backendAppointment.ACTIVITY_LID,
  activityName: backendAppointment.activityName ?? backendAppointment.ACTIVITY_NAME,
  activityPreparation: backendAppointment.preparation ?? backendAppointment.activityPreparation ?? backendAppointment.ACTIVITY_PREPARATION,
  resourceDescription: backendAppointment.resourceDescription ?? backendAppointment.RESOURCE_DESCRIPTION,
  insuranceId: backendAppointment.insuranceId ?? backendAppointment.INSURANCE_LID,
  locationId: backendAppointment.locationId ?? backendAppointment.LOCATION_LID,
  userId: backendAppointment.userId ?? backendAppointment.USER_LID,
  userFirstName: backendAppointment.firstName ?? backendAppointment.USER_FIRST_NAME,
  userSecondName: backendAppointment.secondName ?? backendAppointment.USER_SECOND_NAME,
  userEmail: backendAppointment.eMail ?? backendAppointment.userEmail ?? backendAppointment.USER_EMAIL,
  userPhone: backendAppointment.mobilePhone ?? backendAppointment.USER_MOBILE_PHONE,
  examId: backendAppointment.examId ?? backendAppointment.EXAM_ID,
  activityNumericId: backendAppointment.activityNumericId ?? backendAppointment.ACTIVITY_NUMERIC_ID
});

/**
 * Mappa array di Appointment dal backend all'UI
 */
export const mapAppointmentsToUI = (backendAppointments: BackendAppointmentFull[]): AppointmentUI[] => {
  return backendAppointments.map(mapAppointmentToUI);
};

// ============================================================================
// UI TO BACKEND MAPPERS (per chiamate API)
// ============================================================================

/**
 * Converte Availability UI in formato per addAppointment
 */
export const mapAvailabilityForBooking = (
  availability: Availability,
  activity: Activity,
  userInfo: {
    patientId: string;
    firstName: string;
    lastName: string;
    codiceFiscale: string;
    email: string;
    phone?: string;
    dateOfBirth: string;
    gender: string;
  }
) => ({
  app_start_time: availability.startTime,
  app_end_time: availability.endTime,
  app_date: availability.date,
  resource_lid: availability.resourceId,
  activity_lid: availability.activityId,
  insurance_lid: availability.insuranceId,
  location_lid: availability.locationId,
  user_lid: userInfo.patientId,
  user_first_name: userInfo.firstName,
  user_second_name: userInfo.lastName,
  user_id_number: userInfo.codiceFiscale,
  user_email: userInfo.email,
  user_mobile_phone: userInfo.phone || '',
  user_date_of_birth: userInfo.dateOfBirth,
  user_gender: userInfo.gender
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Raggruppa activities per gruppo
 */
export const groupActivitiesByGroup = (activities: Activity[]) => {
  const grouped: { [key: string]: { label: string; items: Activity[] } } = {};

  activities.forEach((activity) => {
    const groupName = activity.group || 'Altro';

    if (!grouped[groupName]) {
      grouped[groupName] = {
        label: groupName,
        items: []
      };
    }

    grouped[groupName].items.push(activity);
  });

  return Object.values(grouped);
};

/**
 * Raggruppa availabilities per data
 */
export const groupAvailabilitiesByDate = (availabilities: Availability[]) => {
  return availabilities.reduce((groups, availability) => {
    const date = availability.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(availability);
    return groups;
  }, {} as { [date: string]: Availability[] });
};

/**
 * Formatta data per display (es. "15 Marzo 2024").
 * Accetta sia ISO (YYYY-MM-DD) sia formato italiano DD/MM/YYYY.
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return "";
  let date: Date;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [d, m, y] = dateString.split("/");
    date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  } else {
    date = new Date(dateString);
  }
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Formatta orario per display (es. "09:00 - 09:30")
 */
export const formatTimeSlot = (startTime: string, endTime: string): string => {
  return `${startTime} - ${endTime}`;
};

/**
 * Calcola se un appuntamento è modificabile
 */
export const isAppointmentEditable = (appointmentDate: string, appointmentTime: string, minHoursThreshold: number = 48): boolean => {
  const appointmentDateTime = new Date(`${appointmentDate}T${appointmentTime}`);
  const now = new Date();
  const hoursDifference = (appointmentDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursDifference >= minHoursThreshold;
};
